import {BreakpointObserver} from '@angular/cdk/layout';
import {HttpClient} from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {Router} from '@angular/router';
import {animate, style, transition, trigger} from '@angular/animations';
import {TUI_ALWAYS_DASHED} from '@taiga-ui/addon-charts/components/axes';
import {readProfileRaw} from '@gatewee/services';
import {GenericCrudService} from '@gatewee/services/generic-crud.service';
import {NotificationService} from '@gatewee/services/notification.service';
import {SettingsService} from '@gatewee/services/core/settings.service';
import {Notification} from '@gatewee/types/notification';
import {RecipientSettingKey} from '@gatewee/types/recipient-setting';
import {RecipientStat} from '@gatewee/types/recipient-stat';
import {CardIncomeOutcome, PaginatedResponse} from '@gatewee/types/system';
import {Transaction} from '@gatewee/types/transaction';
import {Wallet} from '@gatewee/types/wallet';
import {getNotificationHref} from '@gatewee/utils/helpers/get-notification-href';
import {pollWhileVisible} from '@gatewee/utils/helpers/poll-while-visible';
import {ProviderPipe} from '@gatewee/utils/pipes/provider.pipe';
import {TranslateService} from '@ngx-translate/core';
import {
  catchError,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import {TransactionsService} from '../../_services/transactions/transactions.service';
import {PixAreaComponent} from '../../elements/pix-area/pix-area.component';
import {BankSlipAreaComponent} from '../../elements/bank-slip-area/bank-slip-area.component';

/** How often the dashboard silently refreshes while the tab is visible. */
const dashboardPollIntervalMs = 30_000;

interface DashboardDataSnapshot {
  wallets: Wallet[];
  transactions: PaginatedResponse<Transaction>;
  stats: RecipientStat[];
}

interface UnreadNotificationSnapshot {
  latest: Notification | null;
  unreadCount: number;
}

export interface InvoiceByPayment {
  paymentMethod: string;
  totalAmount: number;
  percentage: number;
}

/** Calendar-month competence periods (not rolling day ranges). */
export type DashboardCompetenceMonth = 'current' | 'previous' | 'twoMonthsAgo';

export type DashboardWalletKey = number | 'aggregated';

interface DashboardWalletOption {
  key: DashboardWalletKey;
  label: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  standalone: false,
  animations: [
    trigger('mobileTabPanel', [
      transition(':enter', [
        style({opacity: 0, transform: 'translateX(10px)'}),
        animate(
          '0.28s cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({opacity: 1, transform: 'translateX(0)'}),
        ),
      ]),
      transition(':leave', [
        animate(
          '0.22s ease',
          style({opacity: 0, transform: 'translateX(-10px)'}),
        ),
      ]),
    ]),
    trigger('txRow', [
      transition(':enter', [
        style({opacity: 0, transform: 'translateY(-10px)'}),
        animate(
          '0.32s cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({opacity: 1, transform: 'translateY(0)'}),
        ),
      ]),
      transition(':leave', [
        animate(
          '0.2s ease',
          style({opacity: 0, transform: 'translateY(-6px)'}),
        ),
      ]),
    ]),
    trigger('notifCard', [
      transition(':enter', [
        style({opacity: 0, transform: 'translateY(-8px)'}),
        animate(
          '0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({opacity: 1, transform: 'translateY(0)'}),
        ),
      ]),
      transition(':leave', [
        animate(
          '0.2s ease',
          style({opacity: 0, transform: 'translateY(-6px)'}),
        ),
      ]),
    ]),
  ],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private walletsService: GenericCrudService<Wallet>;
  private recipientsStatsService: GenericCrudService<RecipientStat>;

  @ViewChild('pixArea') pixArea?: PixAreaComponent;
  @ViewChild('bankSlipArea') bankSlipArea?: BankSlipAreaComponent;

  selectedPeriod: '7' | '30' | '90' = '7';
  selectedMonth: DashboardCompetenceMonth = 'current';

  /** Mobile tab: revenue / chart + payment mix vs activity (balance + feed). */
  mobileDashboardTab: 'faturamento' | 'atividade' = 'faturamento';

  /** Used with *ngIf so tab panels animate only on mobile (desktop keeps both in DOM). */
  isMobile =
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 767px)').matches;

  private readonly destroy$ = new Subject<void>();

  /** Fires an immediate silent refresh (e.g. when the PIX drawer closes). */
  private readonly refreshTrigger$ = new Subject<void>();

  /** Stable formatter reference so [gwCountUpFormatter] isn't recreated per CD. */
  readonly formatBrl = (value: number): string =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  trackByTxId(_index: number, tx: Transaction): number | string {
    return tx.id;
  }

  trackByPageIndex(index: number): number {
    return index;
  }

  trackByWalletKey(
    _index: number,
    option: DashboardWalletOption,
  ): DashboardWalletKey {
    return option.key;
  }

  constructor(
    private readonly transactionsService: TransactionsService,
    private readonly translateService: TranslateService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly cdr: ChangeDetectorRef,
    private readonly notificationService: NotificationService,
    private readonly router: Router,
    private readonly settingsService: SettingsService,
    http: HttpClient,
    private readonly providerPipe: ProviderPipe,
  ) {
    this.walletsService = new GenericCrudService<Wallet>(
      '/wallets/recipient',
      http,
    );

    this.recipientsStatsService = new GenericCrudService<RecipientStat>(
      '/recipients-stats/recipient',
      http,
    );
  }

  recipientType = '';

  wallets: Wallet[] = [];
  /** Active wallet on the balance card; ignored when only one wallet exists. */
  activeWalletKey: DashboardWalletKey | null = null;
  transactions?: PaginatedResponse<Transaction>;
  recipientStatsHistory?: RecipientStat[];

  incomeData: CardIncomeOutcome = {type: '', items: []};
  outgoingData: CardIncomeOutcome = {type: '', items: []};

  readonly paymentMethodIcons: Record<string, string> = {
    pix: '@phi.pix-logo',
    credit_card: '@phi.creditCard',
    bank_slip: '@phi.file',
    slipix: '@phi.file',
  };

  readonly paymentMethodI18nKeys: Record<string, string> = {
    pix: 'features.dashboard.recipient.paymentMethods.pix',
    credit_card: 'features.dashboard.recipient.paymentMethods.credit_card',
    bank_slip: 'features.dashboard.recipient.paymentMethods.bank_slip',
    slipix: 'features.dashboard.recipient.paymentMethods.slipix',
  };

  /** Cached last-period amount and % change to avoid repeated getter calls in template. */
  incomeLastMonth: {amount: number; change: number} = {amount: 0, change: 0};
  outgoingLastMonth: {amount: number; change: number} = {amount: 0, change: 0};

  latestUnreadNotification: Notification | null = null;
  unreadNotificationCount = 0;

  translatedCardsTexts = {
    transactions: '',
    revenueGenerated: '',
    approvalRate: '',
    income: '',
    outgoing: '',
  };

  ngOnInit(): void {
    this.breakpointObserver
      .observe('(max-width: 767px)')
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isMobile = state.matches;
      });

    this.translateLabels();
    this.fetchDashboardData();
    this.fetchUnreadNotifications();
    this.setupAutoRefresh();

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.translateLabels();
        this.cdr.markForCheck();
      });

    this.recipientType = JSON.parse(readProfileRaw()).recipient?.type ?? '';
  }

  /**
   * Silent, non-blocking refresh triggered by: the periodic poller (paused while
   * the tab is hidden), a tab-focus regain, and PIX drawer closes. `switchMap`
   * keeps only the latest in-flight request so bursts never overlap, and no
   * skeleton is shown because `wallets`/`transactions` are already populated.
   */
  private setupAutoRefresh(): void {
    const poll$ = pollWhileVisible({
      intervalMs: dashboardPollIntervalMs,
      immediate: false,
    }).pipe(map(() => undefined));

    merge(poll$, this.refreshTrigger$)
      .pipe(
        switchMap(() =>
          forkJoin({
            dashboard: this.buildDashboardData$(),
            notifications: this.buildUnreadNotifications$(),
          }),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({dashboard, notifications}) => {
        this.applyDashboardData(dashboard);
        this.applyUnreadNotifications(notifications);
      });
  }

  /** Refresh triggered when the PIX drawer closes (dismiss or completed transfer). */
  onPixDrawerOpenChange(open: boolean): void {
    if (!open) {
      this.refreshTrigger$.next();
    }
  }

  onBillPaymentDrawerOpenChange(open: boolean): void {
    if (!open) {
      this.refreshTrigger$.next();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  translateLabels(): void {
    const keys = Object.keys(this.translatedCardsTexts);
    const observables = keys.map(key =>
      this.translateService.get('features.dashboard.cards.' + key),
    );

    forkJoin(observables).subscribe(values => {
      keys.forEach((key, index) => {
        this.translatedCardsTexts[
          key as keyof typeof this.translatedCardsTexts
        ] = values[index];
      });
    });
  }

  fetchDashboardData(filters?: Record<string, unknown>): void {
    this.buildDashboardData$(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe(snapshot => this.applyDashboardData(snapshot));
  }

  /** Combined wallets + transactions + stats fetch for a silent refresh. */
  private buildDashboardData$(
    filters?: Record<string, unknown>,
  ): Observable<DashboardDataSnapshot> {
    return forkJoin({
      wallets: this.walletsService
        .findAll(undefined, true)
        .pipe(catchError(() => of(this.wallets))),
      transactions: this.transactionsService
        .findAllPaginated(
          0,
          10,
          {
            ...filters,
            options: {
              relations: {
                invoice: {
                  client: true,
                },
              },
            },
          },
          true,
        )
        .pipe(
          catchError(() =>
            of(
              this.transactions ??
                ({
                  records: [],
                  count: 0,
                } as unknown as PaginatedResponse<Transaction>),
            ),
          ),
        ),
      stats: this.recipientsStatsService
        .findAll(
          {
            options: {
              order: {
                periodDate: 'DESC',
              },
            },
          },
          true,
        )
        .pipe(catchError(() => of(this.recipientStatsHistory ?? []))),
    });
  }

  private applyDashboardData(snapshot: DashboardDataSnapshot): void {
    this.wallets = snapshot.wallets ?? [];
    this.initializeWalletSelection();
    this.transactions = snapshot.transactions;
    this.applyStats(snapshot.stats ?? []);
    this.cdr.markForCheck();
  }

  private applyStats(data: RecipientStat[]): void {
    this.recipientStatsHistory = data;
    this.incomeData.type = this.translatedCardsTexts.income;
    this.outgoingData.type = this.translatedCardsTexts.outgoing;

    this.incomeData.items = data.map(item => ({
      label: `${item.year}-${item.month < 10 ? `0${item.month}` : item.month}`,
      amount: item.incomeGrossVolume,
    }));
    this.outgoingData.items = data.map(item => ({
      label: `${item.year}-${item.month < 10 ? `0${item.month}` : item.month}`,
      amount: item.cashOutSettledVolume,
    }));
    this.updateIncomeOutgoingLastMonth();
  }

  get invoicesByPayment(): InvoiceByPayment[] {
    const volumeByMethod = this.selectedStat?.invoiceVolumeByMethod ?? {};
    const keys = Object.keys(volumeByMethod);
    const totalVolume = keys.reduce(
      (sum, k) => sum + (volumeByMethod[k] ?? 0),
      0,
    );
    if (totalVolume === 0) return [];
    return keys
      .map(method => ({
        paymentMethod: method,
        totalAmount: volumeByMethod[method] ?? 0,
        percentage: Math.round(
          ((volumeByMethod[method] ?? 0) / totalVolume) * 100,
        ),
      }))
      .filter(inv => inv.totalAmount > 0)
      .sort((a, b) => b.totalAmount - a.totalAmount);
  }

  private updateIncomeOutgoingLastMonth(): void {
    this.incomeLastMonth = this.computeLastMonth(this.incomeData?.items ?? []);
    this.outgoingLastMonth = this.computeLastMonth(
      this.outgoingData?.items ?? [],
    );
  }

  private computeLastMonth(items: {amount: number}[]): {
    amount: number;
    change: number;
  } {
    if (items.length === 0) return {amount: 0, change: 0};
    const amount = items[items.length - 1]?.amount ?? 0;
    let change = 0;
    if (items.length >= 2) {
      const prev = items[items.length - 2].amount;
      if (prev !== 0) {
        change = parseFloat((((amount - prev) / prev) * 100).toFixed(2));
      }
    }
    return {amount, change};
  }

  get hasMultipleWallets(): boolean {
    return this.wallets.length > 1;
  }

  get isAggregatedWalletView(): boolean {
    return this.hasMultipleWallets && this.activeWalletKey === 'aggregated';
  }

  get walletSelectorOptions(): DashboardWalletOption[] {
    if (!this.hasMultipleWallets) {
      return [];
    }

    const walletOptions = this.wallets.map(wallet => ({
      key: wallet.id as DashboardWalletKey,
      label: this.resolveWalletLabel(wallet),
    }));

    return [
      {
        key: 'aggregated',
        label: this.translateService.instant(
          'features.dashboard.recipient.walletSelector.aggregated',
        ),
      },
      ...walletOptions,
    ];
  }

  get walletCarouselIndex(): number {
    const index = this.walletSelectorOptions.findIndex(
      opt => opt.key === this.activeWalletKey,
    );
    return index >= 0 ? index : 0;
  }

  walletForKey(key: DashboardWalletKey): Wallet | undefined {
    if (!this.wallets.length) {
      return undefined;
    }

    if (key === 'aggregated') {
      return this.buildAggregatedWallet();
    }

    return this.wallets.find(w => w.id === key);
  }

  goToWalletSlide(index: number): void {
    const option = this.walletSelectorOptions[index];
    if (!option) {
      return;
    }

    this.activeWalletKey = option.key;
    this.cdr.markForCheck();
  }

  get wallet(): Wallet | undefined {
    if (!this.wallets.length) {
      return undefined;
    }

    if (!this.hasMultipleWallets) {
      return this.wallets[0];
    }

    if (this.activeWalletKey === 'aggregated') {
      return this.buildAggregatedWallet();
    }

    const selected = this.wallets.find(w => w.id === this.activeWalletKey);
    return selected ?? this.wallets[0];
  }

  selectPreviousWallet(): void {
    this.cycleWalletSelection(-1);
  }

  selectNextWallet(): void {
    this.cycleWalletSelection(1);
  }

  private initializeWalletSelection(): void {
    if (!this.wallets.length) {
      this.activeWalletKey = null;
      return;
    }

    if (!this.hasMultipleWallets) {
      this.activeWalletKey = this.wallets[0].id;
      return;
    }

    const currentStillValid =
      this.activeWalletKey === 'aggregated' ||
      (typeof this.activeWalletKey === 'number' &&
        this.wallets.some(w => w.id === this.activeWalletKey));

    if (currentStillValid) {
      return;
    }

    const primary = this.wallets.find(w => w.isPrimary);
    this.activeWalletKey = (primary ?? this.wallets[0]).id;
  }

  private cycleWalletSelection(direction: 1 | -1): void {
    const options = this.walletSelectorOptions;
    if (!options.length) {
      return;
    }

    const currentIndex = options.findIndex(
      opt => opt.key === this.activeWalletKey,
    );
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = (safeIndex + direction + options.length) % options.length;
    this.activeWalletKey = options[nextIndex].key;
    this.cdr.markForCheck();
  }

  private buildAggregatedWallet(): Wallet {
    const base = this.wallets.find(w => w.isPrimary) ?? this.wallets[0];
    const sum = (pick: (wallet: Wallet) => number) =>
      this.wallets.reduce((total, wallet) => total + (+pick(wallet) || 0), 0);

    return {
      ...base,
      availableBalance: sum(w => w.availableBalance),
      pendingBalance: sum(w => w.pendingBalance),
      withheldBalance: sum(w => w.withheldBalance),
    };
  }

  private resolveWalletLabel(wallet: Wallet): string {
    const customLabel = wallet.label?.trim();
    if (customLabel) {
      return customLabel;
    }

    if (wallet.pool?.label?.trim()) {
      return wallet.pool.label.trim();
    }

    if (wallet.integration?.provider) {
      return this.providerPipe.transform(wallet.integration.provider);
    }

    return this.translateService.instant(
      'features.dashboard.recipient.walletSelector.walletFallback',
      {id: wallet.id},
    );
  }

  get hasTransactions(): boolean {
    return !!this.transactions && (this.transactions.count ?? 0) > 0;
  }

  readonly horizontalLinesHandler = TUI_ALWAYS_DASHED;

  readonly chartLegendI18nKeys: string[] = [
    'features.dashboard.recipient.chart.legend.totalRevenue',
    'features.dashboard.recipient.chart.legend.withdrawals',
    'features.dashboard.recipient.chart.legend.fees',
  ];
  readonly chartColors: string[] = ['#447DF7', '#F74444', '#F7A944'];

  private readonly monthAbbreviationsPt = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ] as const;

  private readonly monthAbbreviationsEn = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ] as const;

  private monthAbbrevList(): readonly string[] {
    const lang = this.translateService.currentLang ?? '';
    return lang.startsWith('en')
      ? this.monthAbbreviationsEn
      : this.monthAbbreviationsPt;
  }

  activeChartLegend = [true, true, true];

  readonly txPageSize = 5;
  txPage = 0;

  get txTotalPages(): number {
    const count = this.transactions?.records?.length ?? 0;
    return Math.ceil(count / this.txPageSize) || 1;
  }

  get txPageRecords(): Transaction[] {
    const records = this.transactions?.records ?? [];
    const start = this.txPage * this.txPageSize;
    return records.slice(start, start + this.txPageSize);
  }

  goToTxPage(page: number): void {
    if (page >= 0 && page < this.txTotalPages) {
      this.txPage = page;
    }
  }

  get filteredChartHistory(): RecipientStat[] {
    if (!this.recipientStatsHistory?.length) return [];
    return [...this.recipientStatsHistory].sort(
      (a, b) => a.year - b.year || a.month - b.month,
    );
  }

  private rawChartSeriesRows(): ReadonlyArray<readonly number[]> {
    if (!this.filteredChartHistory.length) return [];
    return this.filteredChartHistory.map(s => [
      +s.incomeGrossVolume,
      +s.cashOutSettledVolume,
      +s.platformFeesRevenue,
    ]);
  }

  /** Bar chart values with inactive legend series zeroed out. */
  get chartValue(): ReadonlyArray<readonly number[]> {
    return this.rawChartSeriesRows().map(col =>
      col.map((v, i) => (this.activeChartLegend[i] ? v : 0)),
    );
  }

  get chartMax(): number {
    let max = 0;
    for (const col of this.chartValue) {
      for (const v of col) {
        if (v > max) max = v;
      }
    }
    return max || 1;
  }

  get currentMonthAbbrevLabel(): string {
    const months = this.monthAbbrevList();
    return months[new Date().getMonth()];
  }

  get previousMonthAbbrevLabel(): string {
    const months = this.monthAbbrevList();
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return months[d.getMonth()];
  }

  get twoMonthsAgoAbbrevLabel(): string {
    const months = this.monthAbbrevList();
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    return months[d.getMonth()];
  }

  get chartAxisXLabels(): string[] {
    if (!this.filteredChartHistory.length) return [];
    const months = this.monthAbbrevList();
    return this.filteredChartHistory.map(s => months[s.month - 1] ?? '');
  }

  get chartAxisYLabels(): string[] {
    const max = this.chartMax;
    const steps = 4;
    const labels: string[] = [];
    for (let i = steps; i >= 0; i--) {
      const val = (max / steps) * i;
      labels.push(this.formatCurrency(val));
    }
    return labels;
  }

  onChartLegendToggle(index: number): void {
    const next = this.activeChartLegend.map((v, i) => (i === index ? !v : v));
    if (!next.some(Boolean)) {
      return;
    }
    this.activeChartLegend = next;
  }

  formatCurrency(value: number): string {
    if (value >= 1_000_000) return `R$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `R$${(value / 1_000).toFixed(0)}K`;
    return `R$${value.toFixed(0)}`;
  }

  private statForMonth(
    month: DashboardCompetenceMonth,
  ): RecipientStat | undefined {
    if (!this.recipientStatsHistory?.length) return undefined;
    const offset = month === 'current' ? 0 : month === 'previous' ? 1 : 2;
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    return this.recipientStatsHistory.find(
      s => s.year === target.getFullYear() && s.month === target.getMonth() + 1,
    );
  }

  get selectedStat(): RecipientStat | undefined {
    return this.statForMonth(this.selectedMonth);
  }

  /** Competence month label for KPI header (e.g. Mar/2026). */
  get selectedCompetenceLabel(): string {
    const s = this.selectedStat;
    if (!s) {
      return '';
    }
    const months = this.monthAbbrevList();
    const abbr = months[s.month - 1] ?? '';
    return `${abbr}/${s.year}`;
  }

  paymentMethodLabelKey(method: string): string {
    return (
      this.paymentMethodI18nKeys[method] ??
      'features.dashboard.recipient.paymentMethods.unknown'
    );
  }

  openPixDrawer(): void {
    this.pixArea?.openPageDrawer();
  }

  openBillPaymentDrawer(): void {
    this.bankSlipArea?.openPageDrawer();
  }

  /** Contas quick action: requires businessRules.enableBankSlipPayment. */
  get isBankSlipPaymentEnabled(): boolean {
    return !!this.settingsService.get(
      'businessRules',
      RecipientSettingKey.ENABLE_BANK_SLIP_PAYMENT,
    );
  }

  /** Wallet id for bill payment when a concrete wallet slide is active. */
  get billPaymentWalletId(): number | null {
    if (
      typeof this.activeWalletKey === 'number' &&
      Number.isFinite(this.activeWalletKey)
    ) {
      return this.activeWalletKey;
    }
    const primary = this.wallets.find(w => w.isPrimary) ?? this.wallets[0];
    return primary?.id ?? null;
  }

  applyFilters(filters: Record<string, unknown>): void {
    this.fetchDashboardData(filters);
  }

  removeFilters(): void {
    this.fetchDashboardData();
  }

  fetchUnreadNotifications(): void {
    this.buildUnreadNotifications$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(snapshot => this.applyUnreadNotifications(snapshot));
  }

  private applyUnreadNotifications(snapshot: UnreadNotificationSnapshot): void {
    this.latestUnreadNotification = snapshot.latest;
    this.unreadNotificationCount = snapshot.unreadCount;
    this.cdr.markForCheck();
  }

  private buildUnreadNotifications$(): Observable<UnreadNotificationSnapshot> {
    return forkJoin({
      page: this.notificationService
        .findAllPaginated('recipient', 0, 1, {read: false}, true)
        .pipe(catchError(() => of({records: [], count: 0, unreadCount: 0}))),
      unread: this.notificationService
        .getUnreadCount('recipient', true)
        .pipe(catchError(() => of({unreadCount: 0}))),
    }).pipe(
      switchMap(({page, unread}) => {
        const unreadRecords = (page.records ?? []).filter(
          n => n.readAt === null,
        );
        const unreadCount =
          page.unreadCount ?? unread.unreadCount ?? unreadRecords.length;

        if (unreadRecords[0] || unreadCount === 0) {
          return of({
            latest: unreadRecords[0] ?? null,
            unreadCount,
          });
        }

        return this.notificationService
          .findAllPaginated('recipient', 0, 20, undefined, true)
          .pipe(
            catchError(() => of({records: [], count: 0, unreadCount: 0})),
            switchMap(fallback => {
              const fallbackUnread = (fallback.records ?? []).filter(
                n => n.readAt === null,
              );
              return of({
                latest: fallbackUnread[0] ?? null,
                unreadCount:
                  fallback.unreadCount ??
                  unread.unreadCount ??
                  fallbackUnread.length,
              });
            }),
          );
      }),
    );
  }

  get latestNotificationHref(): string | null {
    return this.latestUnreadNotification
      ? getNotificationHref(this.latestUnreadNotification)
      : null;
  }

  openNotification(notification: Notification): void {
    const href = getNotificationHref(notification);
    if (!href) {
      return;
    }

    this.notificationService
      .markAsRead('recipient', notification.id, true)
      .pipe(catchError(() => of(notification)))
      .subscribe(() => {
        void this.router.navigateByUrl(href);
      });
  }
}
